import React, { useEffect, useState, useMemo } from "react";
import { useSalesReports } from "../../hooks/useSalesReports";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { Box, Typography, CircularProgress } from "@mui/material";
import CommonTable from "../CommonTable";

export default function AdminSalesDashboard({ filters }) {
  const {
    fetchSummary,
    fetchTrend,
    fetchBranchGradeBreakdown,
    fetchBranchWise,
    fetchGradeWise,
    fetchPaymentModeSummary,
    fetchVoucherSummary,
  } = useSalesReports();

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [branchChart, setBranchChart] = useState([]);
  const [gradeChart, setGradeChart] = useState([]);
  const [branchGrades, setBranchGrades] = useState([]);
  const [paymentModeSummary, setPaymentModeSummary] = useState([]);
  const [voucherSummary, setVoucherSummary] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSummary(filters),
      fetchTrend(filters),
      fetchBranchWise(filters),
      fetchGradeWise(filters),
      fetchBranchGradeBreakdown(filters),
      fetchPaymentModeSummary(filters),
      fetchVoucherSummary(filters),
    ])
      .then(([s, t, b, g, bg, pm, vs]) => {
        setSummary(s);
        setTrend(t);
        setBranchChart(b);
        setGradeChart(g);
        setBranchGrades(bg);
        setPaymentModeSummary(pm);
        setVoucherSummary(vs);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const sortedBranchChart = useMemo(() => {
    return [...branchChart].sort((a, b) =>
      a.branchName.localeCompare(b.branchName)
    );
  }, [branchChart]);

  if (loading) return <CircularProgress />;

  return (
    <Box p={2}>
      {/* 🔹 Summary Cards */}
      <Box display="flex" gap={2} mb={3}>
        <StatCard
          label="Total Sales"
          value={new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
          }).format(summary.totalAmount || 0)}
        />
        <StatCard
          label="No. of Transactions"
          value={summary.totalTransactions}
        />
        <StatCard
          label="Total Discount Vouchers"
          value={new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
          }).format(voucherSummary?.totalVoucherAmount?.toFixed(0) || 0)}
        />
      </Box>

      {/* 🔹 Trend Chart */}
      <Typography variant="h6">Sales Trend</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={trend}>
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="totalAmount"
            stroke="#8884d8"
            name="Amount ₹"
          />
          <Line
            type="monotone"
            dataKey="totalTransactions"
            stroke="#82ca9d"
            name="Transactions"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* 🔹 Branch-wise Chart */}
      <Typography variant="h6" mt={4}>
        Branch-wise Sales
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={sortedBranchChart}>
          <XAxis dataKey="branchName" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="totalAmount" fill="#8884d8" name="Amount ₹" />
          <Bar dataKey="totalTransactions" fill="#82ca9d" name="Transactions" />
        </BarChart>
      </ResponsiveContainer>

      {/* 🔹 Grade-wise Chart */}
      <Typography variant="h6" mt={4}>
        Grade-wise Sales
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={gradeChart}>
          <XAxis dataKey="grade" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="totalAmount" fill="#8884d8" name="Amount ₹" />
          <Bar dataKey="totalTransactions" fill="#82ca9d" name="Transactions" />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="h6" mt={4}>
        Payment Mode Distribution
      </Typography>

      <CommonTable
        columns={[
          { label: "Payment Mode", field: "paymentMode" },
          {
            label: "Amount (₹)",
            field: "totalAmount",
            getSortValue: (row) => row.totalAmount,
          },
          {
            label: "Transactions",
            field: "totalTransactions",
            getSortValue: (row) => row.totalTransactions,
          },
        ]}
        rows={paymentModeSummary}
      />

      {/* 🔹 Branch → Grade Table */}
      <Typography variant="h6" mt={4}>
        Branch-Grade Breakdown
      </Typography>
      <CommonTable
        columns={[
          { label: "Branch", field: "branchName" },
          { label: "Grade", field: "grade" },
          {
            label: "Amount (₹)",
            field: "totalAmount",
            getSortValue: (row) => row.totalAmount ?? 0,
          },
          {
            label: "Transactions",
            field: "totalTransactions",
            getSortValue: (row) => row.totalTransactions ?? 0,
          },
        ]}
        rows={branchGrades.flatMap((bg) =>
          bg.grades.map((g) => ({
            branchName: bg.branchName,
            grade: g.grade,
            totalAmount: g.totalAmount,
            totalTransactions: g.totalTransactions,
          }))
        )}
      />
    </Box>
  );
}

function StatCard({ label, value }) {
  return (
    <Box p={2} borderRadius={2} bgcolor="#f1f1f1" minWidth={150}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}
